"use client"

import { useState, useEffect, useRef } from "react"
import { serverNow } from "@/lib/clock-sync"

export interface PaymentDeadlineState {
  expired: boolean
  hours: number
  minutes: number
  label: string
}

/**
 * Tracks a payment deadline countdown.
 * Returns null before first tick, then a state object with hours/minutes/expired/label.
 * Calls onExpired once when deadline passes.
 */
export function usePaymentDeadline(
  deadline: string | null,
  onExpired?: () => void
): PaymentDeadlineState | null {
  const [state, setState] = useState<PaymentDeadlineState | null>(null)
  const onExpiredRef = useRef(onExpired)
  onExpiredRef.current = onExpired

  useEffect(() => {
    if (!deadline) { setState(null); return }

    let t: ReturnType<typeof setInterval> | undefined
    const update = () => {
      const diff = new Date(deadline).getTime() - serverNow()
      if (diff <= 0) {
        setState({ expired: true, hours: 0, minutes: 0, label: "Payment deadline expired" })
        onExpiredRef.current?.()
        if (t) clearInterval(t)
        return true
      }
      const h = Math.floor(diff / 3_600_000)
      const m = Math.floor((diff % 3_600_000) / 60_000)
      setState({
        expired: false,
        hours: h,
        minutes: m,
        label: h > 0 ? `${h}h ${m}m left to pay` : `${m}m left to pay`,
      })
      return false
    }

    if (update()) return // already expired
    const diff = new Date(deadline).getTime() - serverNow()
    const interval = diff < 60_000 ? 1_000 : 30_000
    t = setInterval(update, interval)
    return () => clearInterval(t)
  }, [deadline])

  return state
}
